'use client'

import { Divider, Skeleton, Stack, Text, Tooltip } from '@mantine/core'
import { useGetMedicationOptionCashPriceQuery } from '@/lib/store/api/medicationOptionPricingApi'
import { useGetSkuPricesQuery } from '@/lib/store/api/skuOptionsApi'
import {
    resolveMedicationOptionVisitCost,
    skuPricesForId,
} from '@/lib/productDetailPage/resolveMedicationOptionVisitCost'
import { isAuthenticatedViaMiddlewareCookie } from '@/lib/isAuthenticatedViaMiddlewareCookie'
import { humanizePrice } from '@/lib/utilities'
import classes from './MedicationPriceCard.module.css'

const INSURANCE_TOOLTIP =
    "Your insurance price is set by your specific plan, so we can only check it once you have a prescription. After your visit, we'll check your plan and show your insurance price."

interface MedicationPriceCardProps {
    skuId: number
}

/**
 * Three prices live here per the pricing design: the visit cost footer comes from
 * sku_options/prices — unauthenticated shows typical copay; authenticated shows
 * `est. insurance price` or `est. price` from the primary/min visit path. The drug's
 * insurance price isn't estimable yet (pharmacy-priced, static tooltip), and the drug
 * cash ("without insurance") price comes from MedicationOptionSkuPriceEstimator via
 * the medication_option_skus cash_price endpoint. The dotted-underlined "Available
 * after visit" text is itself the tooltip trigger, matching the mock — there's no
 * separate info icon.
 */
export function MedicationPriceCard({ skuId }: MedicationPriceCardProps) {
    // Zip isn't wired into pricing yet (see MedicationOptionCashPriceParams), so it's
    // deliberately not resolved or passed here: including it in the query args would
    // give every zip resolution its own RTK Query cache entry, flashing this card back
    // to its loading skeleton each time a zip resolves after the price already rendered.
    // new_storefront_pdp_viewed fires from getMedicationOptionCashPrice's onQueryStarted
    // in medicationOptionPricingApi.ts once the price resolves.
    const { data, isFetching } = useGetMedicationOptionCashPriceQuery({ skuId: String(skuId) })
    const {
        currentData: visitPricesResponse,
        isFetching: isVisitPriceFetching,
        isError: isVisitPriceError,
    } = useGetSkuPricesQuery(String(skuId))

    // Both signals must agree before showing authenticated copy. Cookie matches the
    // header's logged-out chrome; the prices payload's `authenticated` matches how
    // the backend priced the response (typical copay vs patient-specific).
    const isLoggedIn =
        isAuthenticatedViaMiddlewareCookie() && Boolean(visitPricesResponse?.authenticated)
    const isVisitCostLoading = isVisitPriceFetching && !visitPricesResponse
    // Logged-out visitors always get typical-copay copy (resolver falls back to
    // TYPICAL_VISIT_COPAY_CENTS when the SKU has no insurance amount — e.g. seed
    // medication_option SKUs without video/econsult). Skip resolve only while the
    // first prices request is still in flight.
    const visitCost =
        isVisitCostLoading
            ? null
            : !isLoggedIn
              ? resolveMedicationOptionVisitCost(
                    !isVisitPriceError ? skuPricesForId(visitPricesResponse, skuId) : null,
                    false,
                )
              : !isVisitPriceError && visitPricesResponse
                ? resolveMedicationOptionVisitCost(skuPricesForId(visitPricesResponse, skuId), true)
                : null

    return (
        <div className={classes.card}>
            <div className={classes.priceRow}>
                <Stack gap={4} align="center" className={classes.priceColumn}>
                    <Text fz={12} fw="var(--gm-weight-text, 450)" c="var(--gm-foreground-fg-tertiary)">
                        Without insurance
                    </Text>
                    {isFetching ? (
                        <Skeleton height={24} width={64} />
                    ) : (
                        <Text fz={20} fw="var(--gm-weight-text-bold, 600)" c="var(--gm-foreground-fg-primary)" lh={1.2}>
                            {formatCashPrice(data?.total_price_cents ?? null)}
                        </Text>
                    )}
                </Stack>
                <Divider orientation="vertical" className={classes.divider} />
                <Stack gap={4} align="center" className={classes.priceColumn}>
                    <Text fz={12} fw="var(--gm-weight-text, 450)" c="var(--gm-foreground-fg-tertiary)">
                        With insurance
                    </Text>
                    <Tooltip
                        label={INSURANCE_TOOLTIP}
                        multiline
                        w={252}
                        withArrow
                        // Mantine's default only opens on hover/focus, which a touch screen
                        // never triggers — add touch so a tap opens it on mobile.
                        events={{ hover: true, focus: true, touch: true }}
                        classNames={{ tooltip: classes.tooltip, arrow: classes.tooltipArrow }}
                    >
                        <Text
                            component="button"
                            type="button"
                            fz={15}
                            fw={400}
                            c="var(--gm-foreground-fg-primary)"
                            className={classes.availableAfterVisit}
                        >
                            Available after visit
                        </Text>
                    </Tooltip>
                </Stack>
            </div>
            {isVisitCostLoading ? (
                <Text
                    ta="center"
                    fz={12}
                    fw="var(--gm-weight-text, 450)"
                    c="var(--gm-foreground-fg-tertiary)"
                    className={classes.copayFooter}
                    data-testid="medication-visit-cost-footer"
                >
                    <Skeleton height={14} width={180} mx="auto" />
                </Text>
            ) : visitCost ? (
                <Text
                    ta="center"
                    fz={12}
                    fw="var(--gm-weight-text, 450)"
                    c="var(--gm-foreground-fg-tertiary)"
                    className={classes.copayFooter}
                    data-testid="medication-visit-cost-footer"
                >
                    + {humanizePrice(visitCost.amountCents)} {visitCost.label} for visit
                </Text>
            ) : null}
        </div>
    )
}

function formatCashPrice(totalPriceCents: number | null): string {
    return totalPriceCents != null ? humanizePrice(totalPriceCents) : '-'
}
